/*
 * PrdDraftProgressCard — Slice 12.
 *
 * Replaces the silent ~60s window between "User clicked Draft PRD"
 * and "PRD ready · Open workspace" with a live stage indicator. Polls
 * /api/intake/home/{clientId} every 2.5s and reads:
 *   engagement.routing_decision.prd_draft_status = { stage, kind, done }
 *
 * The validx-side draftPRDFromOrbitDossier emits four milestones:
 *   PRD_DRAFT_STARTED      → stage = "Reading your context"
 *   PRD_DRAFT_PROGRESS     → stage = "Drafting the spec"
 *   PRD_DRAFT_PROGRESS     → stage = "Structuring sections"
 *   PRD_DRAFT_READY        → stage = "PRD ready" + done = true
 *
 * Aibot's webhook receiver merges each event into the routing_decision
 * JSONB so the polling read is just a field lookup. When done=true OR
 * the engagement gains a validx_project_id, the card transitions to a
 * terminal "Open workspace" state.
 *
 * Brand: compact 18-20px padding, Plex Mono eyebrow, Plex Serif italic
 * stage line (editorial moment), no spinner, no emoji, sentence case.
 * Stage transitions with a subtle opacity fade so the user feels
 * forward motion without a busy indicator. Brand kit calls out exactly
 * this pattern: "Easing: ease, ease-out for scroll reveals. No custom
 * cubic-beziers. Durations 0.2s (hover) to 0.65s (scroll-in)."
 */

import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';

import type {
  PrdDraftProgressPayload,
  PrdDraftStatus,
} from '../../lib/dossier-types';
import { fetchHome, type HomePayload } from '../../lib/intakeApi';


export interface PrdDraftProgressCardProps {
  payload: PrdDraftProgressPayload;
  clientId?: string;
  /** Fires ONCE the moment the draft is detected done. The parent clears
   *  its mid-flight flag AND pushes the inline review card (which carries
   *  the workspace / dashboard actions). Does NOT open or navigate. */
  onDone?: () => void;
  disabled?: boolean;
  /** Poll only while this card is the live draft (the latest turn). A
   *  historical card from a restored thread must NOT poll — with several
   *  in a thread that hammered /api/intake/home every 2.5s each for the
   *  full 120s timeout. Default true for backwards compatibility. */
  active?: boolean;
}


const POLL_MS = 2500;
const POLL_TIMEOUT_MS = 120_000;  // 120s — drafting the full spec can run past 60s
const FALLBACK_STAGE = 'Drafting your PRD';


export const PrdDraftProgressCard: React.FC<PrdDraftProgressCardProps> = ({
  payload,
  clientId,
  onDone,
  disabled,
  active = true,
}) => {
  const [status, setStatus] = useState<PrdDraftStatus | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const doneFiredRef = useRef(false);

  useEffect(() => {
    if (!clientId || !active) return;
    let cancelled = false;
    const startedAt = Date.now();

    const tick = async () => {
      if (cancelled) return;
      try {
        const home: HomePayload = await fetchHome(clientId);
        const rd = (home.engagement?.routing_decision ?? {}) as Record<string, unknown>;
        const ps = rd.prd_draft_status as PrdDraftStatus | undefined;
        if (ps) setStatus(ps);
        // Capture the project id whenever it lands, but DO NOT transition
        // on it. The validx workspace is provisioned on-demand the moment
        // the draft kicks off, so the project id arrives almost instantly —
        // long before the PRD content exists. Gating "ready" on the id
        // opened an empty workspace + flashed an empty PRD card. The only
        // truthful "ready" signal is prd_draft_status.done, which the
        // PRD_DRAFT_READY webhook sets together with the full document.
        const pid =
          home.engagement?.validx_project_id
          ?? home.builds?.[0]?.project_id
          ?? null;
        if (pid) setProjectId((prev) => prev ?? pid);
        if (ps?.done) {
          // Done — stop polling. Render the terminal two-button footer.
          // We never auto-open; the user chooses workspace vs dashboard.
          if (!doneFiredRef.current) {
            doneFiredRef.current = true;
            onDone?.();
          }
          return;
        }
      } catch (err) {
        console.warn('[PrdDraftProgressCard] poll failed', err);
      }
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        setTimedOut(true);
        return;
      }
      setTimeout(tick, POLL_MS);
    };
    setTimeout(tick, POLL_MS);
    return () => { cancelled = true; };
  }, [clientId, active]);

  // "Ready" is strictly the done signal — NOT the presence of a project id.
  const ready = Boolean(status?.done);
  const stageText = ready
    ? 'PRD ready'
    : (status?.stage || FALLBACK_STAGE);

  return (
    <div
      className={
        'aa-prd-progress'
        + (ready ? ' aa-prd-progress--ready' : '')
        + (timedOut ? ' aa-prd-progress--timeout' : '')
      }
    >
      <header className="aa-prd-progress-header">
        <span className="aa-prd-progress-eyebrow">Drafting PRD</span>
        <h3 className="aa-prd-progress-title">{payload.title}</h3>
      </header>

      <div className="aa-prd-progress-stage-wrap">
        <span
          key={stageText}
          className={
            'aa-prd-progress-stage'
            + (ready ? ' aa-prd-progress-stage--ready' : '')
          }
        >
          {ready ? (
            <>
              <Check size={13} strokeWidth={2.5} className="aa-prd-progress-check" />
              {stageText}
            </>
          ) : (
            <>
              <span className="aa-prd-progress-pulse" aria-hidden="true" />
              {stageText}
            </>
          )}
        </span>
      </div>

      {timedOut && !ready && (
        <p className="aa-prd-progress-timeout-line">
          Still working on it. Refresh the page in a moment to pick up the workspace link.
        </p>
      )}

      {ready && (
        <p className="aa-prd-progress-ready-line">
          Your PRD is ready — review it just below.
        </p>
      )}
    </div>
  );
};

export default PrdDraftProgressCard;
